import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Allowlist of image hosts accepted in <meta og:image> (Phase 3.11)
const ALLOWED_IMG_HOSTS = [
  'mid2bid.com', 'cdn.mid2bid.com',
  'emergentagent.com', 'emergent.host',
  'customer-assets.emergentagent.com',
];

function safeImage(url) {
  if (!url || typeof url !== 'string') return null;
  // Inline data URIs are safe by definition (no network fetch)
  if (url.startsWith('data:image/')) return url;
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return null;
    return ALLOWED_IMG_HOSTS.some(
      (h) => u.hostname === h || u.hostname.endsWith('.' + h)
    )
      ? url
      : null;
  } catch {
    return null;
  }
}

/**
 * SEOHead component - Adds dynamic Open Graph meta tags for link previews
 * @param {string} type - Content type: 'news', 'tenders', 'projects', 'companies', 'contacts', 'crm', 'reports'
 * @param {string} id - The item's unique ID
 * @param {object} fallback - Fallback metadata if API call fails
 */
const SEOHead = ({ type, id, fallback = {} }) => {
  const [metadata, setMetadata] = useState(null);
  
  const defaultMeta = {
    title: fallback.title || 'Mid2Bid | GCC Tender Intelligence',
    description: fallback.description || 'Affordable business intelligence for GCC construction, infrastructure, and energy markets.',
    image: fallback.image || `${window.location.origin}/mid2bid-og-image.png`,
    url: window.location.href
  };

  useEffect(() => {
    if (type && id) {
      // Fetch OG metadata from backend
      fetch(`${API_URL}/api/og/${type}/${id}`)
        .then(res => res.json())
        .then(data => {
          if (data && data['og:title']) {
            setMetadata(data);
          }
        })
        .catch(err => {
          console.log('OG metadata fetch failed, using fallback:', err);
        });
    }
  }, [type, id]);

  const rawMeta = metadata ? {
    title: metadata['og:title'],
    description: metadata['og:description'],
    image: metadata['og:image'],
    url: metadata['og:url']
  } : defaultMeta;

  const meta = {
    ...rawMeta,
    image: safeImage(rawMeta.image) || safeImage(defaultMeta.image) || defaultMeta.image,
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{meta.title}</title>
      <meta name="title" content={meta.title} />
      <meta name="description" content={meta.description} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={meta.url} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={meta.image} />
      <meta property="og:site_name" content="Mid2Bid" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={meta.url} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.image} />
    </Helmet>
  );
};

export default SEOHead;
